import type { GeraeteSpeicher } from './geraetespeicher';
import { sha256Hex } from './sync';

/**
 * R-12 Ü-2 Gerät als Zeuge (docs/golive/r12-ue-umsetzung.md): Die Kasse merkt sich dauerhaft den
 * zuletzt vom Server bestätigten Beleg ihrer Kassen-ID – Belegnummer und SHA-256 der kompakten
 * JWS – und sendet ihn mit jedem Beleg- und Synchronisationsaufruf. Kennt der Server ihn nicht,
 * ist sein Datenstand zurückgefallen: Er sperrt die Kasse (409), nichts wird signiert.
 *
 * Gespeichert im Bereich `meta` des Gerätespeichers (dieselbe IndexedDB wie Puffer und Zähler).
 * Codex R12-4: nur vorwärts – ein Beleg mit kleinerer Kettenposition (`id`) als der gemerkte
 * (Wiederholung „vorhanden“, verspätete Antwort eines anderen Fensters) ersetzt ihn nie; Lesen
 * und Schreiben in einer Transaktion. Pflichtbelege aus `pflichtbelege` liegen in der Kette VOR
 * dem Beleg der Antwort und sind damit mit abgedeckt.
 * Codex R12-8: `mac` ist die Bestätigung des Servers (`zeugeMac`); nur mit ihr sperrt ein
 * Widerspruch die Kasse dauerhaft.
 */
export interface Geraetezeuge {
  belegnummer: string;
  jwsSha256: string;
  id?: number;
  mac?: string;
}

/** Was die Kasse aus einer Antwort des Servers übernimmt. */
export interface BestaetigterBeleg {
  belegnummer: string;
  jwsKompakt: string;
  id?: number;
  mac?: string;
}

export interface GeraetezeugeAblage {
  /** Zeuge der Kasse; null = (noch) keiner bekannt. */
  lese(kassenId: string): Promise<Geraetezeuge | null>;
  /** Bestätigten Beleg merken (nach 2xx mit kompakter JWS); nur vorwärts. */
  merke(kassenId: string, beleg: BestaetigterBeleg): Promise<void>;
}

function schluessel(kassenId: string): string {
  return `geraetezeuge:${kassenId}`;
}

function istZeuge(w: unknown): w is Geraetezeuge {
  return (
    typeof w === 'object' &&
    w !== null &&
    typeof (w as Geraetezeuge).belegnummer === 'string' &&
    typeof (w as Geraetezeuge).jwsSha256 === 'string' &&
    /^[0-9a-f]{64}$/.test((w as Geraetezeuge).jwsSha256)
  );
}

function auswahl(w: Geraetezeuge): Geraetezeuge {
  return {
    belegnummer: w.belegnummer,
    jwsSha256: w.jwsSha256,
    ...(typeof w.id === 'number' && Number.isInteger(w.id) && w.id > 0 ? { id: w.id } : {}),
    ...(typeof w.mac === 'string' && /^[0-9a-f]{64}$/.test(w.mac) ? { mac: w.mac } : {}),
  };
}

export class SpeicherGeraetezeuge implements GeraetezeugeAblage {
  constructor(
    private readonly speicher: GeraeteSpeicher,
    private readonly hash: (text: string) => Promise<string> = sha256Hex,
  ) {}

  async lese(kassenId: string): Promise<Geraetezeuge | null> {
    const w = await this.speicher.transaktion((tx) =>
      tx.get<unknown>('meta', schluessel(kassenId)),
    );
    return istZeuge(w) ? auswahl(w) : null;
  }

  async merke(kassenId: string, beleg: BestaetigterBeleg): Promise<void> {
    const neu = auswahl({
      belegnummer: beleg.belegnummer,
      jwsSha256: await this.hash(beleg.jwsKompakt),
      ...(beleg.id === undefined ? {} : { id: beleg.id }),
      ...(beleg.mac === undefined ? {} : { mac: beleg.mac }),
    });
    await this.speicher.transaktion(async (tx) => {
      const alt = await tx.get<unknown>('meta', schluessel(kassenId));
      const altId = istZeuge(alt) ? auswahl(alt).id : undefined;
      // nur vorwärts (R12-4); N-6: einen Zeugen mit Position ersetzt nie einer ohne (verspätete
      // Antwort eines älteren Serverstands). Nur ohne alte Position gilt „zuletzt gemerkt“.
      if (altId !== undefined && (neu.id === undefined || neu.id <= altId)) {
        return;
      }
      await tx.put('meta', schluessel(kassenId), neu);
    });
  }
}
